import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { Card } from 'react-bootstrap'
import classNames from 'classnames'
import { escapeRegExp, throttle } from 'lodash'

import Searchbar from 'components/Shared/Searchbar'
import { SecondaryButton } from 'components/Shared/Button'
import { useTranslation } from 'react-i18next'
import { RemoveIcon } from 'images'
import useOnClickOutside from 'hooks/UseOnClickOutside'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import { permissionErrorProviderName } from 'domain/platform/permissionErrorTarget'
import Tabs from 'components/Shared/Tabs'
import { useMediaQuery } from 'hooks/useMediaQuery'

const Row = ({ item, onChange, handleRemoveSelectedItem, isSelected, inputType = 'checkbox', name, renderItemExtra, showIdSubtext = true }) => {
  const [isChecked, setIsChecked] = useState(isSelected)

  useEffect(() => {
    setIsChecked(isSelected)
  }, [isSelected])

  const handleCheck = (e) => {
    if (inputType === 'radio') {
      onChange(item)
    } else {
      if (isChecked) {
        handleRemoveSelectedItem(e, item)
      } else {
        onChange(item)
      }
      setIsChecked(!isChecked)
    }
  }

  const inputId = `audience-item-${item.id}-${item.name}`

  return (
    <label htmlFor={inputId} key={item.id} className="search-dropdown_dropdown_results_item">
      <input
        type={inputType}
        id={inputId}
        name={name}
        onChange={handleCheck}
        checked={isChecked}
      />
      <div className="search-dropdown_dropdown_results_item_label">
        <span>{item.name}</span>
        {renderItemExtra?.(item)}
        {showIdSubtext && <span className="search-dropdown_dropdown_results_item_sub">ID: {item.id}</span>}
      </div>
    </label>
  )
}

Row.propTypes = {
  item: PropTypes.object,
  onChange: PropTypes.func,
  handleRemoveSelectedItem: PropTypes.func,
  isSelected: PropTypes.bool,
  inputType: PropTypes.oneOf(['checkbox', 'radio']),
  name: PropTypes.string,
  renderItemExtra: PropTypes.func,
  showIdSubtext: PropTypes.bool,
}

const TryAgain = ({ error, loading, onClick, t, platform }) => {
  const providerName = permissionErrorProviderName(platform)
  return (
    <div className="search-dropdown_try-again">
      {loading ? (
        <LoadingSpinner
          title={t('Recommendations.campaign.fetchLoading', { platform: providerName })}
        />
      ) : (
        <span className="error">{t(error, { provider: providerName })}</span>
      )}
      <SecondaryButton
        onClick={(e) => {
          e.preventDefault()
          onClick()
        }}
        size="small"
        text={t('common.tryAgain')}
        color="navy"
        disabled={loading}
        fullWidth={false}
      />
    </div>
  )
}

TryAgain.propTypes = {
  error: PropTypes.string,
  loading: PropTypes.bool,
  t: PropTypes.func,
  onClick: PropTypes.func,
  platform: PropTypes.oneOf(['meta', 'google']),
}

const SelectedAudiences = ({ defaultValue, audiences, handleRemoveSelectedItem, disabled, isDropdownOpen }) => {
  const ref = useRef(null)
  const classes = ref.current
    ? ref.current.offsetHeight > 40
      ? 'align-self-start mt-2'
      : 'align-self-center'
    : 'align-self-center'

  return (
    <div ref={ref} className="d-flex justify-content-between gap-2">
      {audiences.length ? (
        <div className="search-dropdown_multiselect">
          {audiences.map((audience) => {
            if (audience?.name)
              return (
                <div key={`${audience?.name}-${audience?.id}`} className="search-dropdown_multiselect_title">
                  <span>{audience?.name}</span>
                  {!disabled && (
                    <span className="remove">
                      <RemoveIcon width={14} onClick={(e) => handleRemoveSelectedItem(e, audience)} />
                    </span>
                  )}
                </div>
              )
            return null
          })}
        </div>
      ) : (
        defaultValue
      )}
      {!disabled && (
        <div className={`${classes}`}>
          <div className={`accordion-arrow ${isDropdownOpen ? 'open' : ''}`} />
        </div>
      )}
    </div>
  )
}

SelectedAudiences.propTypes = {
  defaultValue: PropTypes.string,
  audiences: PropTypes.arrayOf(object),
  handleRemoveSelectedItem: PropTypes.func,
  disabled: PropTypes.bool,
  isDropdownOpen: PropTypes.bool,
}

const AudienceList = (props) => {
  const {
    options = [],
    placeholder = 'Search',
    loading,
    error,
    onErrorBtnClick,
    handleSelect,
    handleRemoveSelectedItem,
    selectedAudiences = [],
    inputType = 'checkbox',
    radioName,
    renderItemExtra,
    showIdSubtext = true,
    platform = 'meta',
  } = props

  const [searchTerm, setSearchTerm] = useState('')

  const { t } = useTranslation()

  let filteredItems = useMemo(
    () =>
      options.filter((option) =>
        searchTerm ? option.name.toLowerCase().search(escapeRegExp(searchTerm?.toLowerCase())) > -1 : true,
      ),
    [options, searchTerm],
  )

  const showResults = filteredItems.length || error || loading

  return (
    <div>
      <div className="search-dropdown_dropdown_searchbar">
        <Searchbar
          handleChange={setSearchTerm}
          placeholder={placeholder}
          ariaLabel={placeholder}
          postfixView={<i className="icon fa-solid fa-search" style={{ color: '#ccc' }}></i>}
          defaultValue={searchTerm}
        />
      </div>
              
      <div className="search-dropdown_dropdown_results">
        {(loading || error) && (
          <TryAgain
            loading={loading}
            error={error}
            t={t}
            onClick={onErrorBtnClick}
            platform={platform}
          />
        )}
        
        {showResults ? (
          filteredItems.map((item) => {
            return (
              <Row
                key={item.id}
                item={item}
                onChange={handleSelect}
                handleRemoveSelectedItem={handleRemoveSelectedItem}
                isSelected={selectedAudiences.some((audience) => audience?.id === item?.id)}
                inputType={inputType}
                name={radioName}
                renderItemExtra={renderItemExtra}
                showIdSubtext={showIdSubtext}
              />
            )
          })
        ) : (
          <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
            <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>
              {t('common.noSearchResults')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

AudienceList.propTypes = {
  options: PropTypes.arrayOf(object),
  platform: PropTypes.oneOf(['meta', 'google']),
}

// An independent checkbox multi-select tab whose selection never interacts
// with the main tab0/tab1 pair (which are mutually exclusive by design —
// picking a saved audience clears custom selections, etc). Used for
// selections that can coexist with the main pair, e.g. an exclude list
// alongside an include list, or Google's audience_resource_names alongside
// its audiences list — both just want "some other independent id set", not
// another slot in the tab0/1 mutual-exclusivity dance.
const ExtraTabPanel = ({
  options = [],
  value = null,
  onChange = () => {},
  placeholder,
  loading,
  error,
  onErrorBtnClick,
  renderItemExtra,
  showIdSubtext = true,
  platform = 'meta',
}) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 640px)')
  const [selectedItems, setSelectedItems] = useState([])

  useEffect(() => {
    if (value && options.length) {
      const ids = value.split(',').filter(Boolean)
      const found = ids.map((id) => options.find((option) => option.id === id)).filter(Boolean)
      const currentIds = selectedItems.map((x) => x.id).sort().join(',')
      const newIds = found.map((x) => x.id).sort().join(',')
      if (currentIds !== newIds) {
        setSelectedItems(found)
      }
    } else if (!value && selectedItems.length > 0) {
      setSelectedItems([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options])

  const emit = (items) => {
    const ids = items.map((item) => item.id).filter(Boolean)
    onChange(ids.length ? ids.join(',') : null)
  }

  const handleSelect = (item) => {
    if (!item.id) return
    const updated = [...selectedItems, item]
    setSelectedItems(updated)
    emit(updated)
  }

  const handleRemove = (e, item) => {
    e.stopPropagation()
    const updated = item.id ? selectedItems.filter((x) => x.id !== item.id) : []
    setSelectedItems(updated)
    emit(updated)
  }

  return (
    <AudienceList
      options={options}
      placeholder={isMobile ? t('common.search') : placeholder ?? t('common.search')}
      loading={loading}
      error={error}
      onErrorBtnClick={onErrorBtnClick}
      handleSelect={handleSelect}
      handleRemoveSelectedItem={handleRemove}
      selectedAudiences={selectedItems}
      inputType="checkbox"
      renderItemExtra={renderItemExtra}
      showIdSubtext={showIdSubtext}
      platform={platform}
    />
  )
}

ExtraTabPanel.propTypes = {
  options: PropTypes.arrayOf(object),
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onErrorBtnClick: PropTypes.func,
  renderItemExtra: PropTypes.func,
  showIdSubtext: PropTypes.bool,
  platform: PropTypes.oneOf(['meta', 'google']),
}

const SearchDropdownMultiSelect = ({
  defaultValue = '',
  value = null,
  onChange = () => {},
  options = [],
  savedAudiences = [],
  disabled = false,
  loadingValue = false,
  onErrorBtnClick = () => {},
  loading = false,
  error = null,
  dropdownClasses = '',
  tab0Title,
  tab1Title,
  tab0Placeholder,
  tab1Placeholder,
  showSavedTab = true,
  renderItemExtra,
  showIdSubtext = true,
  extraTabs = [],
  platform = 'meta',
}) => {
  const dropdownRef = useRef(null)
  const accordionRef = useRef(null)
  const dropdownListRef = useRef(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const { t } = useTranslation()
  const [selectedAudiences, setSelectedAudiences] = useState([])
  const [activeTab, setActiveTab] = useState(0)

  const isMobile = useMediaQuery('(max-width: 640px)')

  useEffect(() => {
    const isSavedAudienceChoosen = selectedAudiences.some((audience) => savedAudiences.some((saved) => saved.id === audience.id))
    if (isSavedAudienceChoosen) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [selectedAudiences])

  useEffect(() => {
    if (value && (options.length || savedAudiences.length)) {
      let audienceArray = []
      let audienceIds = value ? value.split(',') : []
      audienceArray = audienceIds.reduce((acc, audienceId) => {
        const foundOption = options.find((option) => option.id === audienceId)
        const foundSavedAudience = savedAudiences.find((audience) => audience.id === audienceId)
        if (foundOption) {
          acc.push(foundOption)
        } else if (foundSavedAudience) {
          acc.push(foundSavedAudience)
        }
        return acc
      }, [])

      if (audienceArray.length > 0) {
        const currentIds = selectedAudiences.map(a => a.id).sort().join(',')
        const newIds = audienceArray.map(a => a.id).sort().join(',')
        if (currentIds !== newIds) {
          setSelectedAudiences(audienceArray)
        }
      }
    } else if (!value && selectedAudiences.length > 0) {
      setSelectedAudiences([])
    }
  }, [value, options, savedAudiences, selectedAudiences])

  useEffect(() => {
    const sideDrawerComponent = document.getElementById('side-drawer-content')

    const handleClick = () => {
      const expectedHeight =
        sideDrawerComponent.offsetHeight - accordionRef.current?.getBoundingClientRect().bottom - 100

      if (dropdownListRef.current) {
        dropdownListRef.current.style.maxHeight = expectedHeight < 180 ? '180px' : expectedHeight + 'px'
        dropdownListRef.current.style.top = accordionRef.current?.offsetHeight
          ? accordionRef.current?.offsetHeight + 'px'
          : '40px'
      }
    }

    const throttledClick = throttle(handleClick, 200)

    if (sideDrawerComponent) {
      throttledClick()
      sideDrawerComponent.addEventListener('click', throttledClick)
    }

    return () => {
      if (sideDrawerComponent) sideDrawerComponent.removeEventListener('click', throttledClick)
    }
  }, [isDropdownOpen, dropdownListRef, accordionRef, selectedAudiences])

  const handleOnChange = (audiences) => {
    let updatedAudience = audiences.map((item) => item.id).filter((el) => el)
    updatedAudience = updatedAudience.length ? updatedAudience.join(',') : null
    onChange(updatedAudience)
  }

  // Handle TC audience selection (radio) - clears all other selections
  const handleSelectTC = () => {
    setSelectedAudiences([])
    handleOnChange([])
  }

  // Handle custom audience selection (checkbox) - clears TC and saved, allows multiple
  const handleSelectCustom = (audience) => {
    if (audience.id) {
      // Remove any saved audiences, keep only custom audiences
      const customOnly = selectedAudiences.filter((aud) => 
        options.some((opt) => opt.id === aud.id)
      )
      const newAudiences = [...customOnly, audience]
      setSelectedAudiences(newAudiences)
      handleOnChange(newAudiences)
      setDropdownOpen(true)
    }
  }

  // Handle saved audience selection (radio) - clears TC and custom, allows only one
  const handleSelectSaved = (audience) => {
    if (audience.id) {
      // Replace all selections with just this saved audience
      const newAudiences = [audience]
      setSelectedAudiences(newAudiences)
      handleOnChange(newAudiences)
      setDropdownOpen(true)
    }
  }

  const handleRemoveSelectedItem = (e, audience) => {
    e.stopPropagation()
    if (audience.__extraTabKey) {
      const extraTab = extraTabs.find((et) => et.key === audience.__extraTabKey)
      if (extraTab) {
        const ids = (extraTab.value || '')
          .split(',')
          .filter(Boolean)
          .filter((id) => id !== audience.id)
        extraTab.onChange(ids.length ? ids.join(',') : null)
      }
      return
    }
    setSelectedAudiences((prevAudiences) => {
      let updatedAudience = audience.id ? prevAudiences.filter((prevAudience) => prevAudience.id !== audience.id) : []
      handleOnChange(updatedAudience)
      return updatedAudience
    })
  }

  // Selections from extraTabs marked showInHeader (e.g. Google Demand Gen's
  // "Include" audiences tab) need to surface in the closed-dropdown header
  // alongside the main tab0/tab1 selection, even though ExtraTabPanel keeps
  // its own independent selection state.
  const extraHeaderAudiences = extraTabs
    .filter((extraTab) => extraTab.showInHeader)
    .flatMap((extraTab) => {
      const ids = (extraTab.value || '').split(',').filter(Boolean)
      return ids
        .map((id) => (extraTab.options || []).find((option) => option.id === id))
        .filter(Boolean)
        .map((option) => ({ ...option, __extraTabKey: extraTab.key }))
    })

  const headerAudiences = [...selectedAudiences, ...extraHeaderAudiences]

  useOnClickOutside(dropdownRef, () => {
    if (isDropdownOpen) {
      setDropdownOpen(false)
    }
  })


  const tabsData = [
    {
      title: tab0Title ?? t('Recommendations.campaign.setup.audience.custom'),
      component: (
        <AudienceList
          options={options}
          placeholder={
            isMobile
              ? t('common.search')
              : tab0Placeholder ?? t('Recommendations.campaign.setup.audience.placeholder.custom')
          }
          loading={loading.custom}
          error={error.custom}
          onErrorBtnClick={onErrorBtnClick}
          handleSelect={handleSelectCustom}
          handleRemoveSelectedItem={handleRemoveSelectedItem}
          selectedAudiences={selectedAudiences.filter((aud) =>
            options.some((opt) => opt.id === aud.id)
          )}
          inputType="checkbox"
          renderItemExtra={renderItemExtra}
          showIdSubtext={showIdSubtext}
          platform={platform}
        />
      ),
    },
  ]

  // tab1 (radio, mutually exclusive with tab0) only makes sense where a
  // "saved audience" concept exists — Meta's saved audiences, or the
  // conversion-goal picker's bundles. Defaults to shown (existing callers
  // never pass this and rely on the tab1Title default below) — Google's
  // audiences picker, which has no such single-select concept, opts out
  // explicitly via showSavedTab={false} rather than this being inferred
  // from tab1Title's presence (which existing callers never set either).
  if (showSavedTab) {
    tabsData.push({
      title: tab1Title ?? t('Recommendations.campaign.setup.audience.saved'),
      component: (
        <AudienceList
          options={savedAudiences}
          placeholder={
            isMobile
              ? t('common.search')
              : tab1Placeholder ?? t('Recommendations.campaign.setup.audience.placeholder.saved')
          }
          loading={loading.saved}
          error={error.saved}
          onErrorBtnClick={onErrorBtnClick}
          handleSelect={handleSelectSaved}
          handleRemoveSelectedItem={handleRemoveSelectedItem}
          selectedAudiences={selectedAudiences.filter((aud) =>
            savedAudiences.some((saved) => saved.id === aud.id)
          )}
          inputType="radio"
          radioName="saved-audience-radio"
          renderItemExtra={renderItemExtra}
          showIdSubtext={showIdSubtext}
          platform={platform}
        />
      ),
    })
  }

  for (const extraTab of extraTabs) {
    tabsData.push({
      title: extraTab.title,
      component: (
        <ExtraTabPanel
          options={extraTab.options}
          value={extraTab.value}
          onChange={extraTab.onChange}
          placeholder={extraTab.placeholder}
          loading={extraTab.loading}
          error={extraTab.error}
          onErrorBtnClick={extraTab.onErrorBtnClick}
          renderItemExtra={extraTab.renderItemExtra ?? renderItemExtra}
          showIdSubtext={extraTab.showIdSubtext ?? showIdSubtext}
          platform={extraTab.platform ?? platform}
        />
      ),
    })
  }

  const isPending = disabled || loadingValue
  const optionsAvailable =
    options.length || extraTabs.some((extraTab) => extraTab.options?.length) || error || loading

  return (
    <div className="search-dropdown" ref={dropdownRef}>
      <div className="accordion">
        <Card
          ref={accordionRef}
          className={classNames({
            disabled: disabled,
            'mb-0': true
          })}
          style={loadingValue ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <Card.Header
            as={Card.Header}
            onClick={() => !isPending && setDropdownOpen((prev) => !prev)}
            className={classNames('text-left', {
              'has-selected-audience': headerAudiences.length > 0
            })}
          >
            <SelectedAudiences
              defaultValue={defaultValue}
              audiences={headerAudiences}
              handleRemoveSelectedItem={handleRemoveSelectedItem}
              disabled={isPending}
              isDropdownOpen={isDropdownOpen}
            />
          </Card.Header>
        </Card>
      </div>

      {isDropdownOpen && !isPending && (
        <div className={`search-dropdown_dropdown ${dropdownClasses}`} ref={dropdownListRef}>
          {optionsAvailable ? (
            <div>
              {defaultValue && (
                <div className="search-dropdown_dropdown_results">
                  <label htmlFor="audience-default-value" className="search-dropdown_dropdown_results_item">
                    <input
                      id="audience-default-value"
                      type="radio"
                      name="audience-type"
                      onChange={handleSelectTC}
                      checked={!headerAudiences.length}
                    />
                    <div className="search-dropdown_dropdown_results_item_default">
                      <span className="search-dropdown_dropdown_results_item_recommended">{defaultValue}</span>

                      <i className="search-dropdown_dropdown_results_item_sub">
                        {t('Recommendations.campaign.setup.audience.recommendedSubtext')}
                      </i>
                    </div>
                  </label>

                  <hr className="divider" />
                </div>
              )}  

              <Tabs tabsData={tabsData} currentTab={activeTab} handleTabChange={setActiveTab} />
            </div>
          ) : (
            <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
              <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>{t('common.noMoreFields')}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SearchDropdownMultiSelect.propTypes = {
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(object),
  onChange: PropTypes.func,
  onErrorBtnClick: PropTypes.func,
  error: PropTypes.shape({
    custom: PropTypes.string,
    saved: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  loadingValue: PropTypes.bool,
  loading: PropTypes.shape({
    custom: PropTypes.bool,
    saved: PropTypes.bool,
  }),
  dropdownClasses: PropTypes.string,
  hasAudienceLimitations: PropTypes.bool,
  tab0Title: PropTypes.string,
  tab1Title: PropTypes.string,
  tab0Placeholder: PropTypes.string,
  tab1Placeholder: PropTypes.string,
  showSavedTab: PropTypes.bool,
  renderItemExtra: PropTypes.func,
  showIdSubtext: PropTypes.bool,
  extraTabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(object),
      value: PropTypes.string,
      onChange: PropTypes.func,
      loading: PropTypes.bool,
      error: PropTypes.string,
      onErrorBtnClick: PropTypes.func,
      renderItemExtra: PropTypes.func,
      showIdSubtext: PropTypes.bool,
      showInHeader: PropTypes.bool,
      platform: PropTypes.oneOf(['meta', 'google']),
    }),
  ),
  platform: PropTypes.oneOf(['meta', 'google']),
}

export default SearchDropdownMultiSelect
